import React, { useMemo } from "react"
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Text,
  Box,
  Heading,
  VStack,
  Input,
  FormControl,
  FormLabel,
  Divider,
  Stack,
} from "@chakra-ui/react"

import Table from "@app/components/Table"

interface ProductSizeFitDrawerProps {
  drawerType?: string
  isOpen: boolean
  onClose: () => void
  handleDrawerType: (drawerType: string) => void
}

type SizeProps = { [key: string]: string }

const ProductSizeFitDrawerNonPDP: React.FC<ProductSizeFitDrawerProps> = ({ drawerType, isOpen, onClose, handleDrawerType }) => {
  const buttonProps = {
    width: `full`,
    py: 2,
    outline: 0,
    _focus: {
      outline: 0,
    },
    _hover: {
      textDecoration: `none`,
    },
    _active: {
      color: `black`,
      _after: {
        opacity: 1,
      },
    },
    _after: {
      content: `""`,
      width: `full`,
      height: `1px`,
      position: `absolute`,
      bottom: 0,
      left: 0,
      backgroundColor: `black`,
      opacity: 0,
      transition: `opacity 0.2s`,
    },
  }

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader p={0}>
          <Flex justifyContent="space-between" flexWrap="wrap" alignItems="center">
            <Box py={2} width={`100%`}>
              <Text fontSize="lg" width={`full`} textAlign={`center`}>
                Size Guide
              </Text>
            </Box>
            <HStack
              spacing={0}
              width={`full`}
              borderBottomWidth={`1px`}
              borderBottomColor={`brand.divider`}
              borderTopWidth={`1px`}
              borderTopColor={`brand.divider`}
            >
              <Button {...buttonProps} isActive={drawerType === "size-guide"} variant="text" onClick={() => handleDrawerType("size-guide")}>
                Size Chart
              </Button>
              <Button
                {...buttonProps}
                isActive={drawerType === "fit-assistant"}
                variant="text"
                onClick={() => handleDrawerType("fit-assistant")}
              >
                Fit Assistant
              </Button>
            </HStack>
          </Flex>
        </DrawerHeader>
        <DrawerBody p={4}>{drawerType === "size-guide" ? <SizeGuide /> : <FitAssistant />}</DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

const SizeGuide = () => {
  const blocks = [
    {
      sizeHeading: "BABY AND TODDLER CLOTHING",
      sizeTable: {
        rows: [
          {
            _key: "5da32a47-9d36-4e9f-b7ae-9112af442a6d",
            _type: "tableRow",
            cells: ["Size", "Weight", "Height", "Age Guide"],
          },
          {
            _key: "67544c2f-a5f4-480f-9fc1-3ee463b210c9",
            _type: "tableRow",
            cells: ["00000", "Up to 3Kg", "Up to 50cm", "Newborn"],
          },
          {
            _key: "d21827e5-b84b-4a6e-baeb-76481824c80e",
            _type: "tableRow",
            cells: ["0000", "Up to 4Kg", "Up to 56cm", "Newborn"],
          },
          {
            _key: "ee2cd19c-8be1-4e42-84a4-49a166a9a869",
            _type: "tableRow",
            cells: ["000", "4-6kg", "Up to 62cm", "0-3 Months"],
          },
          {
            _key: "908517d6-b6f6-4ed4-8ce5-e0547cc0f70c",
            _type: "tableRow",
            cells: ["00", "6-8kg", "Up to 68cm", "3-6 Months"],
          },
          {
            _key: "4b819eae-2755-4842-9362-eb4f7d0848fc",
            _type: "tableRow",
            cells: ["0", "8-10kg", "Up to 76cm", "6-12 Months"],
          },
          {
            _key: "a535bd3d-c1c9-4825-aa96-56aa727308bf",
            _type: "tableRow",
            cells: ["1 Years", "10-12kg", "Up to 84cm", "1 Year"],
          },
          {
            _key: "f1644cad-10bd-457a-b62b-d0bd6aafd711",
            _type: "tableRow",
            cells: ["2 Years", "12-14kg", "Up to 92cm", "2 Years"],
          },
          {
            _key: "4cc9b59e-195d-4d77-a9bc-c1262fd1d349",
            _type: "tableRow",
            cells: ["3 Years", "14-16kg", "Up to 100cm", "3 Years"],
          },
          {
            _key: "478e78d0-d2d3-4d4a-9df8-8668cd26e0fe",
            _type: "tableRow",
            cells: ["4 Years", "16-18kg", "Up to 108cm", "4 Years"],
          },
          {
            _key: "bd96f6e7-a972-4c8f-957f-f320a808a292",
            _type: "tableRow",
            cells: ["5 Years", "18-20kg", "Up to 116cm", "5 Years"],
          },
        ],
      },
    },
    {
      sizeHeading: "BABY AND TODDLER HATS",
      sizeTable: {
        rows: [
          {
            _key: "5da32a47-9d36-4e9f-b7ae-9112af442a6d",
            _type: "tableRow",
            cells: ["SIZE", "HEAD CIRCUMFERENCE"],
          },
          {
            _key: "ee2cd19c-8be1-4e42-84a4-49a166a9a869",
            _type: "tableRow",
            cells: ["XXS (00000)", "Up to 38cm"],
          },
          {
            _key: "908517d6-b6f6-4ed4-8ce5-e0547cc0f70c",
            _type: "tableRow",
            cells: ["XS (0000-000)", "Up to 44cm"],
          },
          {
            _key: "4b819eae-2755-4842-9362-eb4f7d0848fc",
            _type: "tableRow",
            cells: ["S (00-0)", "Up to 48cm"],
          },
          {
            _key: "f1644cad-10bd-457a-b62b-d0bd6aafd711",
            _type: "tableRow",
            cells: ["M (1-2 Years)", "Up to 50cm"],
          },
          {
            _key: "4cc9b59e-195d-4d77-a9bc-c1262fd1d349",
            _type: "tableRow",
            cells: ["L (3-5 Years)", "Up to 52cm"],
          },
        ],
      },
    },
    {
      sizeHeading: "BABY AND TODDLER HAT COMBINATION",
      sizeTable: {
        rows: [
          {
            _key: "5da32a47-9d36-4e9f-b7ae-9112af442a6d",
            _type: "tableRow",
            cells: ["SIZE", "HEAD CIRCUMFERENCE"],
          },
          {
            _key: "ee2cd19c-8be1-4e42-84a4-49a166a9a869",
            _type: "tableRow",
            cells: ["XXS/XS", "Up to 38cm"],
          },
          {
            _key: "908517d6-b6f6-4ed4-8ce5-e0547cc0f70c",
            _type: "tableRow",
            cells: ["XS/S", "Up to 44cm"],
          },
          {
            _key: "4b819eae-2755-4842-9362-eb4f7d0848fc",
            _type: "tableRow",
            cells: ["S/M", "Up to 50cm"],
          },
          {
            _key: "4cc9b59e-195d-4d77-a9bc-c1262fd1d349",
            _type: "tableRow",
            cells: ["M/L", "Up to 52cm"],
          },
        ],
      },
    },
    {
      sizeHeading: "SLEEPING BAG",
      sizeTable: {
        rows: [
          {
            _key: "5da32a47-9d36-4e9f-b7ae-9112af442a6d",
            _type: "tableRow",
            cells: ["SLEEPING BAGS", "SMALL", "MEDIUM", "LARGE"],
          },
          {
            _key: "ee2cd19c-8be1-4e42-84a4-49a166a9a869",
            _type: "tableRow",
            cells: ["Age", "3-12 Months", "12-24 Months", "24-36 Months"],
          },
          {
            _key: "908517d6-b6f6-4ed4-8ce5-e0547cc0f70c",
            _type: "tableRow",
            cells: ["Length (side neck to hem)", "80cm", "90cm", "100cm"],
          },
          {
            _key: "4b819eae-2755-4842-9362-eb4f7d0848fc",
            _type: "tableRow",
            cells: ["Width (at hem)", "90cm", "100cm", "110cm"],
          },
        ],
      },
    },
    {
      sizeHeading: "PUREBABY PRE WALKERS",
      sizeTable: {
        rows: [
          {
            _key: "5da32a47-9d36-4e9f-b7ae-9112af442a6d",
            _type: "tableRow",
            cells: ["SIZE", "EU SIZING", "AGE"],
          },
          {
            _key: "ee2cd19c-8be1-4e42-84a4-49a166a9a869",
            _type: "tableRow",
            cells: ["S", "18", "3-6 months"],
          },
          {
            _key: "908517d6-b6f6-4ed4-8ce5-e0547cc0f70c",
            _type: "tableRow",
            cells: ["M", "20", "6-12 months"],
          },
          {
            _key: "4b819eae-2755-4842-9362-eb4f7d0848fc",
            _type: "tableRow",
            cells: ["L", "22", "12-18 months"],
          },
        ],
      },
    },
    {
      sizeHeading: "LADIES SLEEPWEAR",
      sizeTable: {
        rows: [
          {
            _key: "5da32a47-9d36-4e9f-b7ae-9112af442a6d",
            _type: "tableRow",
            cells: ["SIZE", "AUS SIZING"],
          },
          {
            _key: "ee2cd19c-8be1-4e42-84a4-49a166a9a869",
            _type: "tableRow",
            cells: ["S/M", "8-10"],
          },
          {
            _key: "908517d6-b6f6-4ed4-8ce5-e0547cc0f70c",
            _type: "tableRow",
            cells: ["L/XL", "12-14"],
          },
        ],
      },
    },
    {
      sizeHeading: "PREMATURE",
      sizeTable: {
        rows: [
          {
            _key: "5da32a47-9d36-4e9f-b7ae-9112af442a6d",
            _type: "tableRow",
            cells: ["WEIGHT", "HEIGHT", "HEAD CIRC", "FOOT", "SIZE"],
          },
          {
            _key: "ee2cd19c-8be1-4e42-84a4-49a166a9a869",
            _type: "tableRow",
            cells: ["Up to 1kg", "Up to 40cm", "Up to 28cm", "Up to 7cm", "0000000"],
          },
          {
            _key: "908517d6-b6f6-4ed4-8ce5-e0547cc0f70c",
            _type: "tableRow",
            cells: ["Up to 2kg", "Up to 45cm", "Up to 32cm", "Up to 8cm", "000000"],
          },
          {
            _key: "aec53fcb-59f6-4e75-93ec-c596e7ab6092",
            _type: "tableRow",
            cells: ["Up to 3kg", "Up to 50cm", "Up to 36cm", "Up to 8cm", "00000"],
          },
          {
            _key: "2704cf47-ac94-4f69-8896-b88e220f7bc5",
            _type: "tableRow",
            cells: ["Up to 4kg", "Up to 56cm", "Up to 40cm", "Up to 9cm", "0000"],
          },
        ],
      },
    },
  ]
  return (
    <Box>
      <Box py={2} textAlign={`center`}>
        As babies grow so quickly, often age is not the best indicator of size. Therefore our size charts includes a weight and height guide
        to help you select the right size.
      </Box>

      <VStack width={`100%`} spacing={4}>
        {blocks?.map(({ sizeHeading, sizeTable }) => (
          <Box key={sizeHeading?.toLowerCase()} width={`100%`}>
            {sizeHeading && (
              <Heading as={`h4`} size={`h4`} fontWeight={600} fontSize={`sm`} textAlign={`center`}>
                {sizeHeading}
              </Heading>
            )}
            <Table table={sizeTable} />
          </Box>
        ))}
      </VStack>
    </Box>
  )
}

const FitAssistant: React.FC<FitAssistantProps> = () => {
  const [submitted, setSubmitted] = React.useState(false)
  const [childHeight, setChildHeight] = React.useState(0)
  const [childWeight, setChildWeight] = React.useState(0)
  const [suggestedSizes, setSuggestedSizes] = React.useState(null)

  // Array of objects that contains all size data (Up to and the measurement "kg and Cm" are kept for dev measurement understanding)
  const allSizes = [
    {
      size: "00000",
      weight: "Up to 3Kg",
      height: "Up to 50cm",
    },
    {
      size: "0000",
      weight: "Up to 4Kg",
      height: "Up to 56cm",
    },
    {
      size: "000",
      weight: "4-6kg",
      height: "Up to 62cm",
    },
    {
      size: "00",
      weight: "6-8kg",
      height: "Up to 68cm",
    },
    {
      size: "0",
      weight: "8-10kg",
      height: "Up to 76cm",
    },
    {
      size: "1 Years",
      weight: "10-12kg",
      height: "Up to 84cm",
    },
    {
      size: "2 Years",
      weight: "12-14kg",
      height: "Up to 92cm",
    },
    {
      size: "3 Years",
      weight: "14-16kg",
      height: "Up to 100cm",
    },
    {
      size: "4 Years",
      weight: "16-18kg",
      height: "Up to 108cm",
    },
    {
      size: "5 Years",
      weight: "18-20kg",
      height: "Up to 116cm",
    },
  ]

  // Matches the customer weight and height data against the "allSizes" data above
  const getMatchingSizes = event => {
    event.preventDefault()

    const matchHeight: Array<{ [key: string]: string }> = []
    const matchWeight: Array<{ [key: string]: string }> = []
    const match = []
    if (childHeight) {
      allSizes.map(size => {
        if (size.height) {
          if (size.height.toLowerCase().indexOf("up to") != -1) {
            const thisHeight = size.height.toLowerCase().replace("up to", "").replace("cm", "").replace(" ", "")

            if (+childHeight < +thisHeight && +childHeight + 10 > +thisHeight) {
              matchWeight.push({
                size: size.size,
                weight: size.weight,
                height: size.height,
                reason: "Height",
              })
            }
          }
        }
      })
    }
    if (childWeight) {
      allSizes.map(size => {
        if (size.weight) {
          if (size.weight.toLowerCase().indexOf("up to") != -1) {
            const thisWeight = size.weight.toLowerCase().replace("up to", "").replace("kg", "").replace(" ", "")
            if (childWeight < thisWeight && childWeight + 1 > thisWeight) {
              matchHeight.push({
                size: size.size,
                weight: size.weight,
                height: size.height,
                reason: "Weight",
              })
            }
          } else if (size.weight.toLowerCase().indexOf("-") != -1) {
            const range = size.weight.toLowerCase().replace("kg", "").split("-")
            if (+range[0] <= +childWeight && +childWeight <= +range[1]) {
              matchHeight.push({
                size: size.size,
                weight: size.weight,
                height: size.height,
                reason: "Weight",
              })
            }
          }
        }
      })
    }

    if (matchHeight.length && matchWeight.length) {
      if (matchHeight[0].size == matchWeight[0].size) {
        matchWeight[0].reason = "Height & Weight"
        match.push(matchWeight[0])
      } else {
        match.push(matchHeight[0])
        match.push(matchWeight[0])
      }
    } else if (matchHeight.length) {
      match.push(matchHeight[0])
    } else if (matchWeight.length) {
      match.push(matchWeight[0])
    }
    setSuggestedSizes(match)
    setSubmitted(true)
  }

  return (
    <Box>
      <VStack as={`form`} onSubmit={event => getMatchingSizes(event)} mb={2} pb={2}>
        <FormControl name={`weight`}>
          <FormLabel fontSize={`xs`} fontWeight={600} marginBottom={1} color={`text.primary`}>
            WEIGHT (KG)
          </FormLabel>
          <Input type="number" px={2} onChange={e => setChildWeight(e.target.value)} />
        </FormControl>
        <FormControl name={`weight`}>
          <FormLabel fontSize={`xs`} fontWeight={600} marginBottom={1} color={`text.primary`}>
            HEIGHT (CM)
          </FormLabel>
          <Input type="number" px={2} onChange={e => setChildHeight(e.target.value)} />
        </FormControl>
        <Button type="submit" variant={`outline`} width={`full`}>
          Find Size
        </Button>
      </VStack>
      <Divider mb={2} />
      {submitted ? (
        <Stack divider={<Divider />}>
          {suggestedSizes && suggestedSizes.length ? (
            suggestedSizes.map((size: SizeProps, i: number) => {
              return (
                <Box key={i} my={1}>
                  <Heading as={`h4`} size={`h4`} fontWeight={600}>
                    Size:
                    {size.size}
                  </Heading>
                  <Text pb={2} fontSize={`xs`}>
                    Recommended Size based on {size.reason}
                  </Text>
                  <Text fontSize={`xs`}>Designed For</Text>
                  <Text fontSize={`xs`}>
                    Height:
                    {size.height}
                  </Text>
                  <Text pb={1} fontSize={`xs`}>
                    Weight:
                    {size.weight}
                  </Text>
                </Box>
              )
            })
          ) : (
            <Text fontSize={`sm`}>No suitable options found.</Text>
          )}
        </Stack>
      ) : null}
    </Box>
  )
}

export default ProductSizeFitDrawerNonPDP
